/**
 * @module Services > Feature Services > Preferences Menu Services
 */

import { CloseableComponent, WithAvailable, WithNavigationArea } from '../../common/base';
import { CollectionConfig, CollectionItem, CollectionServices } from '../base/collections';

/**
 * The type of the preferences menu item.
 */
export enum PreferencesMenuItemType {
  /**
   * A divider between preferences menu items.
   */
  DIVIDER = 'DIVIDER',
  /**
   * A preferences menu item that displays a widget.
   */
  WIDGET = 'WIDGET',
  /**
   * A preferences menu item that displays a React component.
   */
  COMPONENT = 'COMPONENT',
  /**
   * A preferences menu item that displays a text and icon.
   */
  CONFIG = 'CONFIG',
}

/**
 * The predefined preferences menu item components.
 */
export enum PreferencesComponent {
  /**
   * Displays the cookie banner menu item.
   */
  CookieBanner = 'CookieBanner',
  /**
   * Displays the date time in  (utc /local or utc with local mode) menu item.
   */
  TimezoneSelector = 'TimezoneSelector',
  /**
   * Displays the appearance (dark /light mode) menu item.
   */
  DarkModeSelector = 'DarkModeSelector',
  /**
   * Displays the language selector menu item.
   */
  LanguageSelector = 'LanguageSelector',
  /**
   * Displays the about menu item.
   */
  AboutDialog = 'AboutDialog',
}

export interface PreferenceSelectorOption {
  id?: string;
  label: string;
  value: string;
}

/**
 * The base configuration object of a preferences menu item.
 */
export interface PreferencesMenuBaseConfig extends CollectionConfig, WithNavigationArea, WithAvailable {
  /**
   * The type of the preferences menu item. It acts as a TypeScript discriminator.
   */
  type: PreferencesMenuItemType;
}

/**
 * The configuration object of a preferences menu item that displayes a widget.
 */
export interface PreferencesMenuWidgetConfig extends PreferencesMenuBaseConfig {
  type: PreferencesMenuItemType.WIDGET;
  /**
   * The name of the widget which need to be displayed.
   * A widget can be registered with the `registerWidget` service.
   */
  widget: string;
  /**
   * Extra CSS class to be added to the widget root.
   */
  className?: string;
}

/**
 * The configuration object of a preferences menu item that displays a text and icon.
 */
export interface PreferencesMenuConfigConfig extends PreferencesMenuBaseConfig {
  type: PreferencesMenuItemType.CONFIG;
  /**
   * The text to be displayed.
   */
  text: string;
  /**
   * The icon to be displayed.
   */
  icon?: string;
  /**
   * Extra CSS class to be added to the widget root.
   */
  className?: string;
}
/**
 * Divider configuration object of a preferences menu item.
 */
export interface PreferencesMenuDividerConfig extends PreferencesMenuBaseConfig {
  type: PreferencesMenuItemType.DIVIDER;
}

/**
 * The configuration object of a preferences menu item that displays a React component.
 *
 * Note: React components can only rendered when the React instances of the host and client
 * microfrontend modules are the same!
 */
export interface PreferencesMenuComponentConfig extends PreferencesMenuBaseConfig {
  type: PreferencesMenuItemType.COMPONENT;
  /**
   * The React component or a predefined preferences component
   */
  Component: CloseableComponent | PreferencesComponent;
  /**
   * The props which can be placed on the React component.
   */
  ComponentProps?: Record<string, any>;
}

/**
 * An initial config to add an item to Preferences Menu.
 * Difference between PreferencesMenuItemConfig and PreferencesMenuItem is basically that
 * PreferencesMenuItems must have an 'id' and 'order' property which is optional but highly recommended in
 * PreferencesMenuItemConfig to help tests, identification of items and proper ordering.
 */
export type PreferencesMenuItemConfig =
  | PreferencesMenuWidgetConfig
  | PreferencesMenuDividerConfig
  | PreferencesMenuComponentConfig
  | PreferencesMenuConfigConfig;

export interface PreferencesMenuItemBase extends CollectionItem, PreferencesMenuBaseConfig {
  order: number;
  id: string;
}

export interface PreferencesMenuWidgetItem
  extends Omit<PreferencesMenuItemBase, 'type'>,
    Omit<PreferencesMenuWidgetConfig, 'id' | 'order'> {
  widget: string;
}

export interface PreferencesMenuConfigItem
  extends Omit<PreferencesMenuItemBase, 'type'>,
    Omit<PreferencesMenuConfigConfig, 'id' | 'order'> {}

export interface PreferencesMenuDividerItem
  extends Omit<PreferencesMenuItemBase, 'type'>,
    Omit<PreferencesMenuDividerConfig, 'id' | 'order'> {}

export interface PreferencesMenuComponentItem
  extends Omit<PreferencesMenuItemBase, 'type'>,
    Omit<PreferencesMenuComponentConfig, 'id' | 'order'> {
  className?: string;
}

export type PreferencesMenuItem =
  | PreferencesMenuDividerItem
  | PreferencesMenuWidgetItem
  | PreferencesMenuComponentItem
  | PreferencesMenuConfigItem;

export type PreferencesServices = CollectionServices<PreferencesMenuItemConfig, PreferencesMenuItem>;

declare module 'types/services/appframe' {
  interface AppFrameServices {
    preferences: PreferencesServices;
  }
}
